import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { Form, Image, Skeleton } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import cn from 'classnames'

import { MultipleEmailInput, Select } from 'components/UI'
import { darkModeSelector, optionsSelector } from 'app-redux/selectors'
import EmailAttachmentToggleRows from './EmailAttachmentToggleRows'
import { getAttachmentPrintHandlers, getRelatedEmailAttachments } from './relatedEmailAttachments'
import styles from './email-method.module.scss'

const EmailMethod = ({
  data,
  emailData,
  goToEmailTemplate,
  loading,
  isMass,
  onPrintPdf,
  withRelationAttachment,
}) => {
  const [showCc, setShowCc] = useState(false)

  const { formatMessage } = useIntl()
  const options = useSelector(optionsSelector)
  const isDarkMode = useSelector(darkModeSelector)

  const relatedAttachments = useMemo(
    () => getRelatedEmailAttachments(data?.relations),
    [data?.relations],
  )

  const invoiceFileName =
    data?.ref_number || emailData?.subject ? `${data?.ref_number || emailData?.subject}.pdf` : null

  const showAttachmentRows = withRelationAttachment && !isMass

  const printHandlers = useMemo(
    () =>
      getAttachmentPrintHandlers({
        invoiceId: data?.id,
        invoicePrintUrlDelivery: data?.print_url_delivery,
        relatedAttachments,
        onPrintInvoicePdf: !isMass ? onPrintPdf : undefined,
      }),
    [data?.id, data?.print_url_delivery, isMass, onPrintPdf, relatedAttachments],
  )

  const toOptions = useMemo(() => {
    return (options.reply_email_address || '')
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean)
      .map((email) => ({ label: email, value: email }))
  }, [options.reply_email_address])

  return (
    <Skeleton active loading={loading}>
      <div className={styles.formSection}>
        {!isMass && (
          <>
            <Form.Item
              label={formatMessage({ id: 'email.from' })}
              name="mail_from"
              className={styles.formGroup}
            >
              <Select
                options={toOptions}
                placeholder={formatMessage({ id: 'financeInvoices.select_email' })}
                className={styles.fromSelect}
                showSearch={false}
              />
            </Form.Item>

            <MultipleEmailInput
              label={formatMessage({ id: 'email.to' })}
              name="mail_to"
              placeholder={formatMessage({ id: 'financeInvoices.add_email' })}
              newUi
              suffixLabel={
                <button
                  type="button"
                  className={cn(styles.ccButton, showCc ? styles.activeCcButton : '')}
                  onClick={() => setShowCc(!showCc)}
                >
                  <span>Cc</span>
                </button>
              }
              rules={[
                {
                  required: true,
                  message: formatMessage({ id: 'profile.please_input_the_email/username' }),
                },
              ]}
            />
            {showCc && (
              <MultipleEmailInput
                label="Cc"
                name="mail_cc"
                newUi
                placeholder={formatMessage({ id: 'financeInvoices.add_email' })}
              />
            )}
          </>
        )}

        {/* Email Preview */}
        <div className={styles.emailPreview}>
          <div className={styles.emailContainer}>
            {/* Gmail Header */}
            <div className={styles.emailHeader}>
              <Image
                src={`/resources/images/${
                  isDarkMode ? 'gmail-logo-dark.png' : 'gmail-logo-light.png'
                }`}
                alt="Gmail"
                preview={false}
                width={70}
              />
              {goToEmailTemplate && (
                <button type="button" className={styles.templateButton} onClick={goToEmailTemplate}>
                  <EditOutlined />
                  <span>{formatMessage({ id: 'wa.change_template' })}</span>
                </button>
              )}
            </div>

            {/* Email Content */}
            <div className={styles.emailContent}>
              <div dangerouslySetInnerHTML={{ __html: emailData?.body || '' }} />

              <EmailAttachmentToggleRows
                invoiceFileName={invoiceFileName}
                relatedAttachments={relatedAttachments}
                showToggles={showAttachmentRows}
                onPrintInvoicePdf={printHandlers.onPrintInvoicePdf}
                onPrintOrderPdf={printHandlers.onPrintOrderPdf}
                onPrintDeliveryPdf={printHandlers.onPrintDeliveryPdf}
              />
            </div>
          </div>
        </div>
      </div>
    </Skeleton>
  )
}

export default EmailMethod
